import React, { memo, useEffect, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Alert, AlertTitle } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import Slide from '@material-ui/core/Slide'

import {
  getNotificationDataSelector,
  getIsShowNotificationSelector,
  hideAlert,
} from 'redux/reducers/notification'
import { useShallowEqualSelector, usePrevious } from 'custom-hooks'

const useClasses = makeStyles(() => ({
  root: {
    position: 'fixed',
    right: 20,
    top: 20,
    zIndex: 9999,
    minWidth: '200px',
  },
}))

const AlertStyled = memo(() => {
  const location = useLocation()
  const prevLocation = usePrevious(location)

  const dispatch = useDispatch()
  const classes = useClasses()

  const _t1 = useRef()
  const _t2 = useRef()

  const { variant, severity, message, alertTitle, timeoutHide } =
    useShallowEqualSelector(getNotificationDataSelector)

  const handleClose = useCallback(() => {
    dispatch(hideAlert())
  }, [dispatch])

  const handleCloseAfterTimeout = useCallback(() => {
    _t2.current = setTimeout(() => {
      dispatch(hideAlert())
    }, 3500)
  }, [dispatch])

  useEffect(() => {
    if (timeoutHide) {
      _t1.current = setTimeout(() => {
        handleClose()
      }, timeoutHide)
    } else {
      window.addEventListener('click', handleCloseAfterTimeout)
    }
    return () => {
      clearTimeout(_t1.current)
      clearTimeout(_t2.current)
      window.removeEventListener('click', handleCloseAfterTimeout)
    }
  }, [timeoutHide, handleClose, handleCloseAfterTimeout])

  useEffect(() => {
    if (
      location?.pathname &&
      prevLocation?.pathname &&
      location?.pathname !== prevLocation?.pathname
    ) {
      handleClose()
    }
    return () => {
      clearTimeout(_t1.current)
      clearTimeout(_t2.current)
      window.removeEventListener('click', handleCloseAfterTimeout)
    }
  }, [
    handleClose,
    handleCloseAfterTimeout,
    location?.pathname,
    prevLocation?.pathname,
  ])

  return (
    <Slide direction="left" in={true} timeout={400}>
      <Alert
        severity={severity}
        classes={classes}
        variant={variant}
        onClose={handleClose}
      >
        {!!alertTitle && <AlertTitle>{alertTitle}</AlertTitle>}
        {message}
      </Alert>
    </Slide>
  )
})

const AlertContainer = () => {
  const isShowNotify = useShallowEqualSelector(getIsShowNotificationSelector)
  return isShowNotify ? <AlertStyled /> : null
}

export default memo(AlertContainer)
